import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import KanbanBoard from "@/components/ats/KanbanBoard";
import { Logo } from "@/components/Logo";

const PIPELINE_ROLES = ["super_admin", "admin_lcs", "recruteur_lcs", "assistant_recruteur", "employeur", "manager_employeur"];

export default function Pipeline() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");

  const allowed = user && PIPELINE_ROLES.includes(user.role);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/applications/received");
      setApplications(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (!loading && user && !allowed) navigate("/dashboard", { replace: true });
  }, [user, loading, allowed, navigate]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const changeStatus = async (applicationId, status) => {
    setApplications((prev) =>
      prev.map((a) => (a.application_id === applicationId ? { ...a, status } : a))
    );
    try {
      await api.patch(`/applications/${applicationId}/status`, { status });
    } catch (err) {
      setError(formatApiError(err));
      load();
    }
  };

  if (!user || !allowed) return null;

  return (
    <div data-testid="pipeline-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} data-testid="pipeline-logo-link" aria-label="Retour au tableau de bord">
            <Logo />
          </button>
          <button
            data-testid="pipeline-back-button"
            onClick={() => navigate("/dashboard")}
            className="text-sm text-slate-600 hover:text-brand-navy border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-100 transition-colors"
          >
            Tableau de bord
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-brand-navy mb-1">Pipeline ATS</h1>
        <p className="text-sm text-slate-500 mb-6">
          Glissez-déposez les candidatures entre les étapes. Chaque changement est enregistré dans l'historique.
        </p>
        {error && (
          <div data-testid="pipeline-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}
        <KanbanBoard applications={applications} onStatusChange={changeStatus} />
      </main>
    </div>
  );
}
